import {
  ChatBubbleOvalLeftEllipsisIcon,
  NewspaperIcon,
  PaperAirplaneIcon,
  Cog8ToothIcon,
  ArrowLeftEndOnRectangleIcon,
  ShieldCheckIcon,
  ComputerDesktopIcon,
} from '@heroicons/react/24/solid';
import { useState, useEffect } from 'react';
import { useLocation, useNavigate, Routes, Route } from 'react-router-dom';
import { logoutUser } from './api/identity';
import RequireAuth from './components/RequireAuth';
import Sidebar from './components/Sidebar';
import AllComments from './pages/AllComments/AllComments';
import CreateAccount from './pages/CreateAccount/CreateAccount';
import DirectMessagesPage from './pages/DirectMessages/DirectMessages';
import Login from './pages/Login/Login';
import NotFound from './pages/NotFound/NotFound';
import Onboarding from './pages/Onboarding/Onboarding';
import RequestResetPassword from './pages/RequestResetPassword/RequestResetPassword';
import ResetPassword from './pages/ResetPassword/ResetPassword';
import Settings from './pages/Settings/Settings';
import Cookies from 'js-cookie';
import logo from './assets/logo.png';
import { Facebook, Instagram, ReplyFill } from 'react-bootstrap-icons';
import { useSocial } from './context/socialProvider';
import { definedRoutes } from './routes';
import { Operations } from './pages/Operations/Operations';
import AdComments from './pages/AdComments/AdComments';
import SocialSettings from './pages/Settings/PlatformSettings';
import Chat from './pages/Chat/chat';
import PostsView from './pages/Posts/PostsView';
import Dashboard from './pages/Dashboard/Dashboard';
import Header from './components/Header';
import { pagesWithSidebar } from './routes';

function App() {
  const [isScreenSmall, setIsScreenSmall] = useState(window.innerWidth <= 960);
  const { currentSocial } = useSocial();

  const handleResize = () => {
    if (window.innerWidth <= 960) {
      setIsScreenSmall(true);
    } else {
      setIsScreenSmall(false);
    }
  };

  useEffect(() => {
    window.addEventListener('resize', handleResize);
  }, []);

  const location = useLocation();
  const navigate = useNavigate();

  // Helper to check if header should be hidden
  const hideHeaderRoutes = [
    /^\/$/,
    /^\/login/,
    /^\/createAccount/,
    /^\/onboarding/,
    /^\/global\/settings$/,
  ];
  const shouldHideHeader = hideHeaderRoutes.some((regex) =>
    regex.test(location.pathname)
  );

  const useSidebar = () => {
    const isPageWithSidebar = pagesWithSidebar.some((route) => {
      const regexRoute = new RegExp(
        `^${route.replace(/:\w+/g, '[^/]+')}/?$`,
        'i'
      );
      const isMatch = regexRoute.test(location.pathname);

      // Debug log
      if (location.pathname.includes('/settings')) {
        console.log(
          `Route: ${route}, RegEx: ${regexRoute}, Path: ${location.pathname}, Match: ${isMatch}`
        );
      }

      return isMatch;
    });

    return isPageWithSidebar;
  };

  // Updated handleLogout function for App.tsx
  const handleLogout = async () => {
    try {
      // Perform regular logout
      await logoutUser();

      // Also sign out from Google if Google Auth is available
      if (
        window.google &&
        window.google.accounts &&
        window.google.accounts.id
      ) {
        window.google.accounts.id.disableAutoSelect();

        // Clear any Google authentication state
        try {
          const auth2 = await window.google.accounts.oauth2.revoke(
            localStorage.getItem('googleToken') || '',
            { async: true }
          );
          console.log('Google sign-out successful');
        } catch (error) {
          console.error('Google sign-out error:', error);
        }
      }

      // Clear local storage and cookies
      localStorage.removeItem('googleToken');

      // Navigate to login page
      navigate('/login');
    } catch (error) {
      console.error('Logout failed', error);
      localStorage.clear();
      navigate('/login');
    }
  };

  return (
    <div className="bg-grey h-screen overflow-hidden">
      <div className="relative h-full">
        <div className="flex h-full bg-black overflow-hidden">
          <div className="">{useSidebar() && <Sidebar />}</div>
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Only show Header if not in excluded routes */}
            {!shouldHideHeader && <Header />}
            <div className="flex-1 min-h-0 overflow-auto">
              <Routes>
                <Route path="/" element={<Login />} />
                <Route path="/login" element={<Login />} />
                <Route path="/createAccount" element={<CreateAccount />} />
                <Route
                  path="/requestResetPassword"
                  element={<RequestResetPassword />}
                />
                <Route path="/reset-password" element={<ResetPassword />} />

                <Route
                  path="/onboarding"
                  element={<RequireAuth>{<Onboarding />}</RequireAuth>}
                />
                <Route
                  path="/:providerId/:providerName/postsview"
                  element={
                    <RequireAuth>
                      <PostsView />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/:providerId/:providerName/postsview/:postID"
                  element={
                    <RequireAuth>
                      <PostsView />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/:providerId/:providerName/allComments"
                  element={
                    <RequireAuth>
                      <AllComments />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/:providerId/:providerName/analytics"
                  element={
                    <RequireAuth>
                      <Dashboard />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/:providerId/:providerName/adComments"
                  element={
                    <RequireAuth>
                      <AdComments />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/:providerId/:providerName/chat"
                  element={
                    <RequireAuth>
                      <Chat />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/:providerId/:providerName/directMessages"
                  element={
                    <RequireAuth>
                      <DirectMessagesPage />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/:providerId/:providerName/directMessages/:pageConvoID"
                  element={
                    <RequireAuth>
                      <DirectMessagesPage />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/global/settings"
                  element={
                    <RequireAuth>
                      <Settings />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/:providerId/:providerName/settings"
                  element={
                    <RequireAuth>
                      <SocialSettings />
                    </RequireAuth>
                  }
                />
                <Route
                  path="/operations"
                  element={
                    <RequireAuth>
                      <Operations />
                    </RequireAuth>
                  }
                />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </div>
          </div>
        </div>
        {isScreenSmall && <ScreenSizeWarning />}
      </div>
    </div>
  );
}

const ScreenSizeWarning = () => {
  return (
    <div className="flex w-screen h-screen bg-grey items-center justify-center absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
      <div className="flex flex-col items-center justify-center">
        <img className={'h-12 mb-4'} src={logo} />
        <span className="text-xl">Please use a larger screen</span>
      </div>
    </div>
  );
};

export default App;
